import {
  JupiterTheme,
  CodeError,
  VisualQueryBuilderIcon,
} from '@jupiterone/jupiter-web-toolkit';
import { ClickAwayListener, Container, Paper } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React from 'react';
import * as MockHook from '../../util/useMockHook';
import useJ1SearchbarLogic, {
  J1SearchbarComponentProps,
} from './useJ1SearchbarLogic';
import HelpIcon from 'react-feather/dist/icons/help-circle';
import ClearSearchesButton from './ClearSearchesButton/ClearSearchesButton';
import LibraryButton from './LibraryButton/LibraryButton';
import SearchAceEditor from './SearchAceEditor/SearchAceEditor';
import SearchIconButton from './SearchIconButton/SearchIconButton';
import SearchOptionsDropdown from './SearchOptionsDropdown/SearchOptionsDropdown';
import SuggestedQuestions from './SuggestedQuestions/SuggestedQuestions';

const useStyles = makeStyles((theme: JupiterTheme) => {
  return createStyles({
    root: {
      marginTop: theme.spacing(2),
      maxWidth: 960,
    },
    hasResults: {
      position: 'absolute',
      zIndex: 3,
    },
    searchContainer: {
      alignSelf: 'flex-start',
      display: 'flex',
      flexFlow: 'column',
      flexGrow: 1,
      position: 'relative',
    },
    paper: {
      alignItems: 'center',
      background: 'none',
      borderRadius: 0,
      boxShadow: 'none',
      display: 'flex',
      position: 'relative',
      '&:before': {
        background: theme.palette.common.white,
        boxShadow: '0px 3px 5px -2px rgba(0, 0, 0, 0.2)',
        borderRadius: 4,
        display: 'block',
        content: `''`,
        height: 60,
        left: 0,
        position: 'absolute',
        top: 0,
        width: '100%',
      },
    },
    levelZero: {
      zIndex: 0,
    },
    loading: {
      marginTop: theme.spacing(-0.25),
    },
    codeError: {
      borderRadius: theme.spacing(0, 0, 0.5, 0.5),
    },
    icon: {
      height: 29,
      width: 29,
      strokeWidth: 2,
    },
    libraryOpen: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      '&$hasResults': {
        maxWidth: 'none',
      },
    },
  });
});

export const J1SearchbarComponent = ({
  errorMessage,
  searchText,
  hasResults,
  includeDeleted,
  libraryOpen,
  suggestedQuestions,
  enabledDropdownOptions,
  autocompleteEnabled,
  showSuggestedQuestions,
  showGradientBackground,
  handleHideDropdown,
  handleShowDropdownOptions,
  handleChangeSearchText,
  handleSearch,
  handleToggleQuestionList,
  handleHideSuggestedQuestions,
  handleSelectQuestion,
  handleIncludeDeletedEntities,
  handleEnableAutocomplete,
  handleOpenVisualQueryBuilder,
  handleToggleJ1QLHelp,
  handleClearSearches,
}: J1SearchbarComponentProps) => {
  const classes = useStyles();
  return (
    <Container
      className={clsx(
        classes.root,
        hasResults && classes.hasResults,
        libraryOpen && classes.libraryOpen
      )}
    >
      <Paper className={classes.paper}>
        {libraryOpen !== undefined && (
          <LibraryButton
            active={libraryOpen}
            onClick={handleToggleQuestionList}
          />
        )}

        <ClickAwayListener onClickAway={handleHideDropdown}>
          <div className={classes.searchContainer}>
            <SearchAceEditor
              value={searchText}
              hasFocus={enabledDropdownOptions}
              onChange={handleChangeSearchText}
              onSearch={handleSearch}
              onFocus={handleShowDropdownOptions}
              autocomplete={autocompleteEnabled}
            />

            {searchText && showSuggestedQuestions && (
              <ClickAwayListener onClickAway={handleHideSuggestedQuestions}>
                <div className={classes.levelZero}>
                  <SuggestedQuestions
                    questions={suggestedQuestions}
                    onSelectQuestion={handleSelectQuestion}
                  />
                </div>
              </ClickAwayListener>
            )}
            <SearchOptionsDropdown
              hidden={!enabledDropdownOptions || !!errorMessage}
              includeDeleted={includeDeleted}
              onIncludeDeleted={handleIncludeDeletedEntities}
              autocompleteEnabled={autocompleteEnabled}
              onEnableAutocomplete={handleEnableAutocomplete}
              labelOnClick={handleShowDropdownOptions}
              showGradientBackground={showGradientBackground}
            />
            {enabledDropdownOptions && errorMessage && (
              <div className={classes.levelZero}>
                <CodeError error={errorMessage} className={classes.codeError} />
              </div>
            )}
          </div>
        </ClickAwayListener>

        {handleOpenVisualQueryBuilder && handleToggleJ1QLHelp && (
          <>
            {' '}
            <SearchIconButton
              icon={<VisualQueryBuilderIcon className={classes.icon} />}
              tooltipTitle="Visual Query Builder"
              onClick={handleOpenVisualQueryBuilder}
            />
            <SearchIconButton
              icon={<HelpIcon className={classes.icon} />}
              tooltipTitle="Help"
              onClick={handleToggleJ1QLHelp}
            />
          </>
        )}
        {hasResults && <ClearSearchesButton onClick={handleClearSearches} />}
      </Paper>
    </Container>
  );
};

export default MockHook.withHookHoc(J1SearchbarComponent, useJ1SearchbarLogic);
